import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useGetCampaign } from "@njoy/controller";

type CampaignData = {
  id: string;
  title: string;
  description: string;
  question: string;
  bannerUrl: string;
  campaignLink: string;
  pollOptions: string[];
  location: string[];
  community: string[];
  campaignType: string;
  colorCode: string;
};

type CampaignFlowContextValue = {
  campaign: CampaignData | null;
  armFromDiscover: () => void;
  dismissCampaign: () => void;
  completeCampaign: () => void;
};

const CampaignFlowContext = createContext<CampaignFlowContextValue | null>(null);

const preloadImage = async (src: string): Promise<void> => {
  const image = new Image();

  await new Promise<void>((resolve) => {
    const finish = () => {
      image.onload = null;
      image.onerror = null;
      resolve();
    };

    image.onload = finish;
    image.onerror = finish;
    image.src = src;

    if (image.complete) {
      finish();
    }
  });

  try {
    if (typeof image.decode === "function") {
      await image.decode();
    }
  } catch {
    // Ignore decode failures and still allow the modal to open.
  }
};

export const CampaignFlowProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const {
    id,
    title,
    description,
    question,
    bannerUrl,
    campaignLink,
    pollOptions,
    location,
    community,
    campaignType,
    colorCode,
    loading,
  } = useGetCampaign();
  const [isArmed, setIsArmed] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [readyCampaign, setReadyCampaign] = useState<CampaignData | null>(null);
  const pollOptionsKey = JSON.stringify(pollOptions);
  const locationKey = JSON.stringify(location);
  const communityKey = JSON.stringify(community);

  useEffect(() => {
    let isActive = true;

    if (loading) {
      return () => {
        isActive = false;
      };
    }

    if (!id) {
      setReadyCampaign(null);
      return () => {
        isActive = false;
      };
    }

    const nextCampaign: CampaignData = {
      id,
      title,
      description,
      question,
      bannerUrl,
      campaignLink,
      pollOptions: JSON.parse(pollOptionsKey) as string[],
      location: JSON.parse(locationKey) as string[],
      community: JSON.parse(communityKey) as string[],
      campaignType,
      colorCode,
    };

    if (campaignType !== "Advert" || !bannerUrl) {
      setReadyCampaign(nextCampaign);
      return () => {
        isActive = false;
      };
    }

    setReadyCampaign(null);

    void (async () => {
      await preloadImage(bannerUrl);

      if (isActive) {
        setReadyCampaign(nextCampaign);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [
    bannerUrl,
    campaignLink,
    campaignType,
    colorCode,
    description,
    id,
    loading,
    locationKey,
    communityKey,
    pollOptionsKey,
    question,
    title,
  ]);

  const armFromDiscover = useCallback(() => {
    setIsArmed(true);
  }, []);

  const dismissCampaign = useCallback(() => {
    setIsClosed(true);
  }, []);

  const completeCampaign = useCallback(() => {
    setIsClosed(true);
  }, []);

  const campaign = isArmed && !isClosed ? readyCampaign : null;

  return (
    <CampaignFlowContext.Provider
      value={{
        campaign,
        armFromDiscover,
        dismissCampaign,
        completeCampaign,
      }}
    >
      {children}
    </CampaignFlowContext.Provider>
  );
};

export const useCampaignFlow = (): CampaignFlowContextValue => {
  const context = useContext(CampaignFlowContext);

  if (!context) {
    throw new Error("useCampaignFlow must be used within CampaignFlowProvider");
  }

  return context;
};
